import { memo, useMemo, useState } from 'react';
import { compactNumber } from '../utils/formatNumber';
import './MapHud.css';

/**
 * Live-map counter HUD: a single translucent "liquid glass" instrument strip
 * (see MapHud.css for the design + the class-collision history).
 *
 * Two scopes with a segmented toggle, defaulting to Now:
 *   Now:   Total (on map) · New · Mil · Msg/s · Max range · No Pos
 * Every Now counter is over the same filtered set the list shows, so
 * Total + No Pos is everything tracked and New/Mil are subsets of it
 * (Mil therefore includes military aircraft with no current position).
 *   Today: Total · New · Msgs · All Time (aircraft) · Max range (today)
 * (A "military today" count isn't in the receiver_statistics payload yet, so
 * Mil appears on Now only.)
 * Every item always renders in a fixed value slot, so the strip never resizes.
 */
function StatsOverlay({
  aircraftWithPositionCount = 0,
  aircraftNoPositionCount = 0,
  activeNewCount = 0,
  militaryCount = 0,
  updatesPerSecond = 0,
  currentMaxRangeKm = 0,
  todayMaxRangeKm = 0,
  receiverStatistics = null,
  selectedReceiver = 'All Receivers',
  receivers = [],
  variant = 'live' // 'live' or 'history'
}) {
  const [scope, setScope] = useState('now'); // 'now' | 'today'

  // Get statistics for the selected receiver
  const selectedReceiverStats = useMemo(() => {
    if (!receiverStatistics || !receiverStatistics.receivers) return null;

    if (selectedReceiver === 'All Receivers') {
      return receiverStatistics.receivers.reduce((acc, receiver) => {
        return {
          totalUniqueAircraftAllTime: acc.totalUniqueAircraftAllTime + (receiver.totalUniqueAircraftAllTime || 0),
          totalUniqueAircraftToday: acc.totalUniqueAircraftToday + (receiver.totalUniqueAircraftToday || 0),
          newAircraftToday: acc.newAircraftToday + (receiver.newAircraftToday || 0),
          totalMessagesToday: acc.totalMessagesToday + (receiver.totalMessagesToday || 0)
        };
      }, {
        totalUniqueAircraftAllTime: 0,
        totalUniqueAircraftToday: 0,
        newAircraftToday: 0,
        totalMessagesToday: 0
      });
    } else {
      const receiver = receivers.find(r => r.receiverName === selectedReceiver);
      if (receiver) {
        return receiverStatistics.receivers.find(
          r => r.receiverUuid === receiver.receiverUuid
        );
      }
    }
    return null;
  }, [receiverStatistics, selectedReceiver, receivers]);

  const item = (label, value, cls = '') => (
    <div className={`map-hud-item ${cls}`.trim()}>
      <span className="map-hud-l">{label}</span>
      <span className="map-hud-v">{value}</span>
    </div>
  );

  const showTabs = variant === 'live';
  const activeScope = showTabs ? scope : 'now';

  return (
    <div className="map-hud">
      {showTabs && (
        <div className="map-hud-tabs">
          {[['now', 'Now'], ['today', 'Today']].map(([id, label]) => (
            <button
              key={id}
              className={`map-hud-tab ${scope === id ? 'active' : ''}`.trim()}
              onClick={() => setScope(id)}
              onTouchEnd={(e) => { e.preventDefault(); setScope(id); }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {activeScope === 'now' && (
        <>
          {item('Total', compactNumber(aircraftWithPositionCount))}
          {item('New', compactNumber(activeNewCount), 'new')}
          {item('Mil', compactNumber(militaryCount))}
          {item('Msg/s', compactNumber(updatesPerSecond))}
          {item('Max km', Math.round(currentMaxRangeKm || 0).toLocaleString())}
          {item('No Pos', compactNumber(aircraftNoPositionCount), 'muted')}
        </>
      )}

      {activeScope === 'today' && (
        <>
          {/* Aircraft totals show FULL numbers (1,743 not 1.7k) — they're the
              headline figures. Message counts stay compact. */}
          {item('Total', (selectedReceiverStats?.totalUniqueAircraftToday || 0).toLocaleString())}
          {item('New', compactNumber(selectedReceiverStats?.newAircraftToday || 0), 'new')}
          {item('Msgs', compactNumber(selectedReceiverStats?.totalMessagesToday || 0))}
          {item('All Time', (selectedReceiverStats?.totalUniqueAircraftAllTime || 0).toLocaleString())}
          {/* Full km, not 1.1k — a range figure is read to the kilometre. */}
          {item('Max km', Math.round(todayMaxRangeKm || 0).toLocaleString())}
        </>
      )}
    </div>
  );
}

export default memo(StatsOverlay);
